/**
 * CompetitionDetailsModal — детали соревнования + управление участниками.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, Stack, Group, Box, Text, Badge, Table, Button,
  Select, ActionIcon, Loader, Center, Divider,
} from '@mantine/core';
import {
  IconPlus, IconTrash, IconTrophy, IconCalendar, IconUsers,
  IconCheck, IconX, IconEdit,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import tokens from '../../theme/designTokens';

const STATUS_LABELS = {
  draft: { label: 'Черновик', color: 'gray' },
  registration_open: { label: 'Регистрация', color: 'green' },
  registration_closed: { label: 'Рег. закрыта', color: 'orange' },
  in_progress: { label: 'Идёт', color: 'blue' },
  completed: { label: 'Завершено', color: 'violet' },
};

const PAYMENT_LABELS = {
  pending: { label: 'Ожидает', color: 'orange' },
  paid: { label: 'Оплачен', color: 'green' },
  not_required: { label: 'Бесплатно', color: 'gray' },
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('ru-RU', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Moscow',
  });
};

const CompetitionDetailsModal = ({
  opened, onClose, competition, onEdit, students = [], subjects = [],
}) => {
  const [participants, setParticipants] = useState([]);
  const [loadingP, setLoadingP] = useState(false);
  const [addStudentId, setAddStudentId] = useState(null);
  const [addSubjectId, setAddSubjectId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editingParticipantId, setEditingParticipantId] = useState(null);
  const [editSubjectId, setEditSubjectId] = useState(null);

  const loadParticipants = useCallback(async () => {
    if (!competition) return;
    setLoadingP(true);
    try {
      const res = await fetch(`/api/competitions/${competition.id}/participants`, { credentials: 'include' });
      if (res.ok) setParticipants(await res.json());
    } catch {} finally { setLoadingP(false); }
  }, [competition]);

  useEffect(() => {
    if (opened && competition) loadParticipants();
  }, [opened, competition, loadParticipants]);

  const handleAddParticipant = async () => {
    if (!addStudentId) {
      notifications.show({ title: 'Ошибка', message: 'Выберите ученика', color: 'red' });
      return;
    }
    setAdding(true);
    try {
      const res = await fetch(`/api/competitions/${competition.id}/participants`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_id: Number(addStudentId),
          subject_id: addSubjectId ? Number(addSubjectId) : null,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Ошибка');
      }
      setAddStudentId(null);
      setAddSubjectId(null);
      loadParticipants();
    } catch (err) {
      notifications.show({ title: 'Ошибка', message: err.message, color: 'red' });
    } finally { setAdding(false); }
  };

  const handleSaveSubject = async (participantId) => {
    try {
      await fetch(`/api/competitions/${competition.id}/participants/${participantId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject_id: editSubjectId ? Number(editSubjectId) : 0 }),
      });
      setEditingParticipantId(null);
      setEditSubjectId(null);
      loadParticipants();
    } catch {}
  };

  const handleRemoveParticipant = (p) => {
    modals.openConfirmModal({
      title: 'Удалить участника?',
      centered: true,
      children: <Text size="sm">Убрать {p.student_name} из соревнования?</Text>,
      labels: { confirm: 'Удалить', cancel: 'Отмена' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await fetch(`/api/competitions/${competition.id}/participants/${p.id}`, {
            method: 'DELETE', credentials: 'include',
          });
          loadParticipants();
        } catch {}
      },
    });
  };

  if (!competition) return null;

  const statusCfg = STATUS_LABELS[competition.status] || STATUS_LABELS.draft;
  const compSubjects = competition.subjects || [];

  // Ученики уже записанные
  const participantStudentIds = new Set(participants.map((p) => p.student_id));
  const availableStudents = students
    .filter((s) => s.is_active && !participantStudentIds.has(s.id))
    .map((s) => ({
      value: String(s.id),
      label: [s.last_name, s.first_name].filter(Boolean).join(' ') || s.email || `#${s.id}`,
    }));

  const subjectOptions = (compSubjects.length > 0 ? compSubjects : subjects).map((s) => ({
    value: String(s.id),
    label: s.name,
  }));

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="sm">
          <IconTrophy size={22} color={tokens.colors.brand.primary} />
          <Text fw={700}>{competition.name}</Text>
        </Group>
      }
      centered
      radius="lg"
      size="xl"
    >
      <Stack gap="md">
        {/* Инфо */}
        <Group gap="lg" wrap="wrap">
          <Badge color={statusCfg.color} variant="filled" radius="xl">
            {statusCfg.label}
          </Badge>
          {competition.is_active && (
            <Badge color="green" variant="light" radius="xl">Активно в ЛК</Badge>
          )}
          <Group gap="xs">
            <IconCalendar size={16} color={tokens.colors.neutral[8]} />
            <Text size="sm" fw={600}>{formatDate(competition.event_date)}</Text>
          </Group>
          {competition.registration_deadline && (
            <Text size="sm" c="dimmed" fw={600}>
              Дедлайн записи: {formatDate(competition.registration_deadline)}
            </Text>
          )}
        </Group>

        {competition.description && (
          <Text size="sm" c="dimmed">{competition.description}</Text>
        )}

        {compSubjects.length > 0 && (
          <Group gap="xs">
            <Text size="sm" fw={600}>Предметы:</Text>
            {compSubjects.map((s) => (
              <Badge key={s.id} variant="filled" color="blue" radius="xl" size="sm">
                {s.name}
              </Badge>
            ))}
          </Group>
        )}

        <Group>
          <Button variant="subtle" size="xs" onClick={() => { onClose(); onEdit(competition); }}>
            Редактировать
          </Button>
        </Group>

        <Divider label={
          <Group gap="xs">
            <IconUsers size={16} />
            <Text fw={700}>Участники ({participants.length})</Text>
          </Group>
        } labelPosition="left" />

        {/* Добавление участника */}
        <Group gap="sm" align="center">
          <Select
            placeholder="Ученик"
            data={availableStudents}
            value={addStudentId}
            onChange={setAddStudentId}
            searchable
            radius="xl"
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Предмет (необяз.)"
            data={subjectOptions}
            value={addSubjectId}
            onChange={setAddSubjectId}
            clearable
            radius="xl"
            style={{ width: 160 }}
          />
          <Button
            leftSection={<IconPlus size={14} />}
            onClick={handleAddParticipant}
            loading={adding}
          >
            Записать
          </Button>
        </Group>

        {/* Таблица участников */}
        {loadingP ? (
          <Center py="md"><Loader size="sm" /></Center>
        ) : participants.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="md">
            Участников пока нет
          </Text>
        ) : (
          <Table striped highlightOnHover style={{ fontSize: 13 }}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Ученик</Table.Th>
                <Table.Th>Предмет</Table.Th>
                <Table.Th>Источник</Table.Th>
                <Table.Th>Оплата</Table.Th>
                <Table.Th style={{ width: 40 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {participants.map((p) => {
                const payCfg = PAYMENT_LABELS[p.payment_status] || PAYMENT_LABELS.pending;
                return (
                  <Table.Tr key={p.id}>
                    <Table.Td>
                      <Text size="sm" fw={600}>{p.student_name || '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      {editingParticipantId === p.id ? (
                        <Group gap={4} align="center" wrap="nowrap">
                          <Select
                            size="xs"
                            placeholder="Предмет"
                            data={subjectOptions}
                            value={editSubjectId}
                            onChange={setEditSubjectId}
                            clearable
                            radius="xl"
                            style={{ width: 130 }}
                          />
                          <ActionIcon size="sm" variant="filled" color="green" radius="xl" onClick={() => handleSaveSubject(p.id)}>
                            <IconCheck size={12} />
                          </ActionIcon>
                          <ActionIcon size="sm" variant="subtle" color="gray" radius="xl" onClick={() => setEditingParticipantId(null)}>
                            <IconX size={12} />
                          </ActionIcon>
                        </Group>
                      ) : (
                        <Group
                          gap={4}
                          align="center"
                          wrap="nowrap"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingParticipantId(p.id);
                            setEditSubjectId(p.subject_id ? String(p.subject_id) : null);
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          {p.subject_name ? (
                            <>
                              <Badge size="xs" variant="filled" color="blue" radius="xl">
                                {p.subject_name}
                              </Badge>
                              <IconEdit size={12} color="#1c7ed6" style={{ flexShrink: 0 }} />
                            </>
                          ) : (
                            <Badge size="xs" variant="light" color="blue" radius="xl">
                              + предмет
                            </Badge>
                          )}
                        </Group>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Badge size="xs" variant="light" color={p.registration_source === 'parent_lk' ? 'violet' : 'gray'} radius="xl">
                        {p.registration_source === 'parent_lk' ? 'ЛК' : 'Менеджер'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="xs" variant="filled" color={payCfg.color} radius="xl">
                        {payCfg.label}
                      </Badge>
                      {p.paid_amount && (
                        <Text span size="xs" c="dimmed" ml={6}>{Number(p.paid_amount).toLocaleString('ru-RU')} ₽</Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <ActionIcon variant="subtle" color="red" size="sm" onClick={() => handleRemoveParticipant(p)}>
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
    </Modal>
  );
};

export default CompetitionDetailsModal;
